import {
    HomeIcon,
    WorkflowIcon,
    BarChart3Icon,
    ShieldCheckIcon,
    Wallet2Icon,
    BookOpenIcon,
} from "lucide-react";
import { NavItem } from "@type/navItems";
import { useEffect, useState } from "react";
import { useUserProfileContext } from "@context/user_profile";
import LayoutWithMinimalSidebarV2 from "@container/layout_v2";
import { SnackbarProvider } from "@hook/use-snackbar";
import { STRIPE_SUBSCRIPTION_ENABLED } from "@util/billing-platform";

// The Knowledge Base list/upload/delete surface is flag-gated so the sidebar
// entry stays aligned with route/backend availability: in any env where the KB
// is intentionally off, we must not show a nav item that leads to a dead page.
const kbEnabled = import.meta.env.VITE_ENABLE_TRAINING_KB === "true";

interface Props {
    withoutHeader?: boolean
    children: React.ReactNode
}

export default function TrainingAuthLayout(props: Props) {
    const [, userInfo] = useUserProfileContext();
    const [navItems, setNavItems] = useState<NavItem[]>([])

    useEffect(() => {
        if (userInfo) {
            if (userInfo.role === "training-agent" && userInfo.company_type === "training-agent") {
                // Two sidebar sections: the core workflow (Dashboard → Reports)
                // and the supporting surfaces (Knowledge Base, Billing). The
                // `group` keys make the sidebar render a thin divider between them.
                setNavItems([
                    {
                        name: "training-dashboard",
                        tooltip: "Training overview",
                        icon: HomeIcon,
                        label: "Dashboard",
                        to: "/dashboard",
                        roles: ["training-agent"],
                        group: "core",
                    },
                    {
                        name: "training-campaigns",
                        tooltip: "Manage roleplay campaigns",
                        icon: WorkflowIcon,
                        label: "Campaigns",
                        to: "/training/campaigns",
                        roles: ["training-agent"],
                        group: "core",
                    },
                    {
                        name: "training-aiqm",
                        tooltip: "AI quality monitoring for calls",
                        icon: ShieldCheckIcon,
                        label: "AI QM",
                        to: "/training/aiqm",
                        roles: ["training-agent"],
                        group: "core",
                    },
                    {
                        name: "training-performance",
                        tooltip: "Team analytics and employee progress",
                        icon: BarChart3Icon,
                        label: "Reports",
                        to: "/training/performance",
                        roles: ["training-agent"],
                        group: "core",
                    },
                    // Knowledge Base — supporting surface, gated behind
                    // VITE_ENABLE_TRAINING_KB so nav visibility tracks the route.
                    ...(kbEnabled ? [{
                        name: "training-knowledge-base",
                        tooltip: "Manage knowledge base documents",
                        icon: BookOpenIcon,
                        label: "Knowledge Base",
                        to: "/training/knowledge-base",
                        roles: ["training-agent"],
                        group: "support",
                    }] : []),
                    // Billing — shared, platform-aware surface (the page picks the
                    // TrainingAuthLayout for training). Flag-gated; off by default
                    // ⇒ no visual change until the epic ships. The "Subscription"
                    // menu is intentionally hidden on all platforms — its surfaces
                    // now live under Billing (the /subscription route still works).
                    ...(STRIPE_SUBSCRIPTION_ENABLED ? [{
                        name: "training-billing",
                        icon: Wallet2Icon,
                        tooltip: "Manage usage and invoices",
                        label: "Billing",
                        to: "/billing",
                        roles: ["training-agent"],
                        group: "support",
                    }] : []),
                ])
            }
        }
    }, [userInfo])

    return (
        <SnackbarProvider>
            <LayoutWithMinimalSidebarV2
                navItems={navItems}
            >
                <div className="px-4 sm:px-16 mx-auto">
                    <div className="mt-0 sm:mt-8">
                        {props.children}
                    </div>
                </div>
            </LayoutWithMinimalSidebarV2>
        </SnackbarProvider>
    );
}
